import { useState } from "react";
import {
  Reclamo,
  Caso,
  Holding,
  Usuario,
  EventoEstado,
  Documento,
} from "../../types";
import { Badge, getEstadoReclamoBadgeVariant } from "../common/Badge";
import { ReclamoDetail } from "./ReclamoDetail";

interface ReclamosViewProps {
  reclamos: Reclamo[];
  casos: Caso[];
  holdings: Holding[];
  usuarios: Usuario[];
  eventosEstado: EventoEstado[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onReclamoUpdate: (reclamo: Reclamo) => void;
  onEventoEstadoCreate: (data: Omit<EventoEstado, "id">) => number;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
}

const ESTADO_RECLAMO_LABELS: Record<string, string> = {
  preparacion: "En Preparacion",
  presentado: "Presentado",
  en_tramite: "En Tramite",
  resuelto: "Resuelto",
  desistido: "Desistido",
};

export function ReclamosView({
  reclamos,
  casos,
  holdings,
  usuarios,
  eventosEstado,
  documentos,
  usuarioActual,
  onReclamoUpdate,
  onEventoEstadoCreate,
  onDocumentoCreate,
}: ReclamosViewProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>("todos");

  const filtered = filtroEstado === "todos"
    ? reclamos
    : reclamos.filter((r) => r.estado === filtroEstado);

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime()
  );

  const selected = reclamos.find((r) => r.id === selectedId);
  const getHoldingNombre = (holdingId: number) =>
    holdings.find((h) => h.id === holdingId)?.nombre || "-";
  const getCasoTitulo = (solicitudId: number) =>
    casos.find((c) => c.id === solicitudId)?.titulo || "-";

  return (
    <div className="flex h-full">
      {/* Left panel - list */}
      <div className="w-96 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Reclamos</h2>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="mt-2 w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="preparacion">En Preparacion</option>
            <option value="presentado">Presentado</option>
            <option value="en_tramite">En Tramite</option>
            <option value="resuelto">Resuelto</option>
            <option value="desistido">Desistido</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">
              No hay reclamos
            </div>
          ) : (
            sorted.map((reclamo) => (
              <div
                key={reclamo.id}
                onClick={() => setSelectedId(reclamo.id)}
                className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                  selectedId === reclamo.id ? "bg-blue-50 border-l-2 border-l-blue-600" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-slate-800 truncate">
                    {reclamo.titulo}
                  </span>
                  <Badge variant={getEstadoReclamoBadgeVariant(reclamo.estado)}>
                    {ESTADO_RECLAMO_LABELS[reclamo.estado] || reclamo.estado}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {getHoldingNombre(reclamo.holdingId)} - Solicitud: {getCasoTitulo(reclamo.solicitudId)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {new Date(reclamo.fechaCreacion).toLocaleDateString("es-CL")}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right panel - detail */}
      <div className="flex-1 overflow-hidden">
        {selected ? (
          <ReclamoDetail
            reclamo={selected}
            caso={casos.find((c) => c.id === selected.solicitudId)}
            holding={holdings.find((h) => h.id === selected.holdingId)}
            usuarios={usuarios}
            eventosEstado={eventosEstado.filter(
              (e) => e.entidadTipo === "reclamo" && e.entidadId === selected.id
            )}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onReclamoUpdate={onReclamoUpdate}
            onEventoEstadoCreate={onEventoEstadoCreate}
            onDocumentoCreate={onDocumentoCreate}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-slate-400">
            <p>Selecciona un reclamo para ver su detalle</p>
          </div>
        )}
      </div>
    </div>
  );
}
